/**
 * useState whose changes are browser-history steps, so the browser's Back
 * button (and a tablet's swipe-back) walks back through Navigator's screens
 * instead of leaving the app.
 *
 *   const [screen, go] = useHistoryState("preRaid", { v: "code" }, { level: 1 });
 *   go({ v: "home" });                    // new history entry
 *   go({ v: "home" }, { replace: true }); // rewrite the current entry
 *
 * Every mounted hook keeps its value under its own key in history.state, so
 * Back restores all of them together. `level` is nesting depth: 0 for the
 * top-level screen, 1 for a module's own screens, 2 for screens inside those.
 * A step drops the keys of deeper levels, so a module opened again later
 * starts fresh instead of restoring a screen from an earlier visit.
 *
 * Values must be small and JSON-safe — names of screens, ids, a section
 * number. Never member answers: browsers keep session history on disk.
 * A restored value can point at data that is no longer loaded (Forward into
 * a closed record, Back into a submitted intake); callers check for that and
 * redirect.
 */

import { useCallback, useEffect, useRef, useState } from "react";

const LEVELS = new Map();   // key -> level, for the hooks currently mounted

const navState = () => (typeof window !== "undefined" && window.history.state?.nav) || {};
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

export function useHistoryState(key, initial, { level = 0, hash } = {}) {
  const initialRef = useRef(initial);
  const [value, setValue] = useState(() => navState()[key] ?? initial);
  const valueRef = useRef(value);
  valueRef.current = value;

  useEffect(() => {
    LEVELS.set(key, level);
    // First visit to this entry: record our value on it so Back can return here.
    if (navState()[key] === undefined) {
      const nav = { ...navState(), [key]: valueRef.current };
      window.history.replaceState({ ...window.history.state, nav }, "");
    }
    const onPop = () => {
      const v = navState()[key];
      setValue(v === undefined ? initialRef.current : v);
    };
    window.addEventListener("popstate", onPop);
    return () => {
      window.removeEventListener("popstate", onPop);
      LEVELS.delete(key);
    };
  }, [key, level]);

  const go = useCallback((next, { replace = false } = {}) => {
    if (!replace && same(next, valueRef.current)) return;
    const nav = Object.fromEntries(Object.entries(navState()).filter(([k]) => k !== key && (LEVELS.get(k) ?? Infinity) < level));
    nav[key] = next;
    const url = hash ? `#/${hash(next)}` : undefined;
    window.history[replace ? "replaceState" : "pushState"]({ ...window.history.state, nav }, "", url);
    valueRef.current = next;
    setValue(next);
  }, [key, level, hash]);

  return [value, go];
}
